import Row from 'components/Row'
import { useRefetchQuote } from 'hooks/useRefetchQuote'
import React from 'react'
import styled from 'styled-components/macro'

const RefetchIconWrapper = styled.div<{ $isFetching?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;

  ${({ $isFetching }) =>
    $isFetching
      ? `
    animation: spinWrapper 1.2s linear infinite;
    @keyframes spinWrapper {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `
      : `
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform: rotate(0deg);
  `}
`

const RefreshSVG = styled.svg`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;

  path {
    fill: ${({ theme }) => theme.textSecondary};
  }
`

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.backgroundOutline};
  margin: 0 0 0 12px;
`

const RefreshIconContainer = styled.div<{ $shouldBounce?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ $shouldBounce }) =>
    $shouldBounce &&
    `
    animation: bounce 0.5s ease-in-out;
    @keyframes bounce {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
    }
  `}
`

const Button = styled.button<{ disabled?: boolean; $isOnCooldown?: boolean }>`
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? 'pointer' : 'pointer')};
  outline: none;
  opacity: ${({ $isOnCooldown }) => ($isOnCooldown ? 0.8 : 1)};
  position: relative;

  :not([disabled]):hover {
    opacity: 0.7;
  }
`

const IconContainer = styled(Row)`
  padding: 6px 4px 6px 8px;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
`

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`

const Tooltip = styled.div<{ $show?: boolean }>`
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 10px;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 6px;
  white-space: nowrap;
  font-size: 12px;
  color: ${({ theme }) => theme.textPrimary};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${({ theme }) => theme.backgroundOutline};
  }
`

interface RefetchButtonProps {
  refetchQuote?: () => void
  isLoading?: boolean
  hasQuote?: boolean
}

export default function RefetchButton({ refetchQuote, isLoading = false, hasQuote = false }: RefetchButtonProps) {
  const { handleRefetch, state } = useRefetchQuote(refetchQuote, isLoading)
  const { isOnCooldown, isFetching, remainingTime, remainingMs, canRefetch } = state

  const [showTooltip, setShowTooltip] = React.useState(false)
  const [shouldBounce, setShouldBounce] = React.useState(false)
  const prevCooldown = React.useRef(isOnCooldown)

  const isDisabled = !canRefetch || isFetching || isOnCooldown || !hasQuote

  // Trigger bounce when cooldown ends
  React.useEffect(() => {
    let timer: NodeJS.Timeout | undefined

    if (prevCooldown.current && !isOnCooldown && !isFetching) {
      setShouldBounce(true)
      timer = setTimeout(() => setShouldBounce(false), 500)
    }

    prevCooldown.current = isOnCooldown

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isOnCooldown, isFetching])

  const getTooltipText = () => {
    if (!refetchQuote) return 'No quote to refetch'
    if (!hasQuote) return 'Enter an amount to get quote'
    if (isFetching) return 'Fetching new quote...'
    if (isOnCooldown && remainingTime > 0)
      return `Refetch available in ${remainingTime} second${remainingTime !== 1 ? 's' : ''}`
    return 'Refetch quote'
  }

  const handleMouseEnter = () => {
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  return (
    <TooltipWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Button disabled={isDisabled} $isOnCooldown={isOnCooldown} onClick={handleRefetch} aria-label="Refetch quote">
        <IconContainer>
          <RefetchIconWrapper $isFetching={isFetching}>
            <RefreshIconContainer $shouldBounce={shouldBounce}>
              <RefreshSVG xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <path d="M 15 3 C 12.053086 3 9.3294211 4.0897803 7.2558594 5.8359375 A 1.0001 1.0001 0 1 0 8.5449219 7.3652344 C 10.27136 5.9113916 12.546914 5 15 5 C 20.226608 5 24.456683 8.9136179 24.951172 14 L 22 14 L 26 20 L 30 14 L 26.949219 14 C 26.441216 7.8348596 21.297943 3 15 3 z M 4.3007812 9 L 0.30078125 15 L 3 15 C 3 21.635519 8.3644809 27 15 27 C 17.946914 27 20.670579 25.91022 22.744141 24.164062 A 1.0001 1.0001 0 1 0 21.455078 22.634766 C 19.72864 24.088608 17.453086 25 15 25 C 9.4355191 25 5 20.564481 5 15 L 8.3007812 15 L 4.3007812 9 z" />
              </RefreshSVG>
            </RefreshIconContainer>
          </RefetchIconWrapper>
          <Separator />
        </IconContainer>
      </Button>
      <Tooltip $show={showTooltip}>{getTooltipText()}</Tooltip>
    </TooltipWrapper>
  )
}
